import { useEffect } from 'preact/hooks'

/**
 * Keep Tab inside an open sheet, and give focus back when it closes (D-104 §2).
 *
 * **Focus lands on the sheet's own title, not on its first control.** That is
 * the precedent `e2e/accessibility.spec.ts` already guards for photo capture:
 * moving focus to a heading makes a screen reader announce what just opened,
 * where moving it to a button announces only the button and leaves the user to
 * work out where they are.
 *
 * **Restoration is to the element that opened it**, captured at open time rather
 * than read back afterwards. `document.activeElement` at close is whatever the
 * sheet last focused, which is the one thing it must not return to.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THE PAGE BEHIND IS NOT MARKED `inert`, AND THAT IS NOT AN OVERSIGHT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The obvious implementation sets `inert` on `#main-content` while a sheet is
 * open. **It cannot work here.** `Screen` renders `<main id="main-content">`,
 * every information control lives inside it, and — with no portal available
 * (`createPortal` is in `preact/compat`, outside the pinned stack) — **so does
 * the sheet.** Marking that container inert makes the sheet's own Close button
 * inert with it.
 *
 * It was written that way first and a component test caught it, which is the
 * cheapest place this could have been caught: in a browser it would have looked
 * like a sheet that renders and then ignores every control in it.
 *
 * So the page behind is held by the two mechanisms that do not depend on
 * ancestry: **`aria-modal="true"` on the dialog**, which is what tells VoiceOver
 * and NVDA to confine themselves to it, and **the Tab cycle below**, which keeps
 * a keyboard inside. That is the conventional pairing for a non-portalled
 * dialog, and it is what the browser suite will check on a device.
 */
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Takes a **ref object, not a getter**.
 *
 * An inline `() => ref.current` is a new function on every render, so it would
 * change the effect's dependencies every render — tearing the trap down and
 * setting it up again each time. That is not merely wasteful: the opener is
 * captured on setup, so re-running recaptures it as whatever is focused *now*,
 * which after the first pass is the sheet's own heading. Focus would then be
 * "restored" to the element being unmounted, and land on `<body>`.
 *
 * Caught by a component test, and worth stating because the bug looks like a
 * focus problem and is actually a dependency-identity one.
 */
export function useFocusTrap(
  active: boolean,
  container: { current: HTMLElement | null },
  options: {
    /**
     * The control to give focus back to, supplied rather than guessed.
     *
     * **`document.activeElement` is not good enough, and a component test is
     * what proved it.** Clicking a button does not focus it in jsdom — and, more
     * importantly, **tapping one does not focus it in iOS Safari**, which is the
     * device this app is designed for. Reading the active element at open time
     * therefore captures `<body>` on the exact platform that matters, and focus
     * would be "restored" to the document root: the defect
     * `e2e/accessibility.spec.ts` already guards for photo capture, reappearing
     * in a new component.
     *
     * The caller knows which control it is. `Button.elementRef` exists for the
     * same reason and says so.
     */
    readonly returnTo?: { current: HTMLElement | null }
  } = {},
): void {
  const returnTo = options.returnTo

  useEffect(() => {
    if (!active) return

    const opener = returnTo?.current ?? (document.activeElement as HTMLElement | null)
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab') return

      const root = container.current
      if (root === null) return

      const focusable = [...root.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
        (element) => element.offsetParent !== null || element === root,
      )
      if (focusable.length === 0) {
        // Nothing to move to; keeping focus where it is beats letting it escape.
        event.preventDefault()
        return
      }

      const first = focusable[0] as HTMLElement
      const last = focusable[focusable.length - 1] as HTMLElement
      const current = document.activeElement

      if (event.shiftKey && (current === first || current === root)) {
        event.preventDefault()
        last.focus()
        return
      }
      if (!event.shiftKey && current === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      document.removeEventListener('keydown', onKeyDown, true)
      /* Restored on the next frame rather than immediately: the sheet's own
         element is still in the document during this cleanup, and focusing past
         it synchronously is undone when it unmounts. */
      if (opener !== null && document.contains(opener)) {
        opener.focus()
      }
    }
  }, [active, container, returnTo])
}
